import { reportError } from "@cabocheck/shared";
import { Popover, PopoverContent, PopoverTrigger } from "@radix-ui/react-popover";
import { isValid } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { PatternFormat } from "react-number-format";
import { cn } from "~/utils";
import { formatDate, parseDate } from "../utils/date";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Input } from "./ui/input";

export function DateInput({ type, value, onChange, tabIndex }: Props) {
  const {
    i18n: { language },
  } = useTranslation();
  const getFormat = () => {
    return type === "daytime" ? "##.##.#### ##:##" : "##.##.####";
  };

  const formatValue = useCallback(() => {
    if (!value) return "";
    return formatDate(value, type === "daytime" ? "daytime" : "day");
  }, [value, type]);

  const handleValueChange = (values: any) => {
    const { value: inputValue } = values;

    if (!inputValue) {
      onChange(undefined);
      return;
    }

    try {
      if (type === "daytime") {
        if (inputValue.length === 12) {
          const parsedDate = parseDate(
            inputValue.replace(/(\d{2})(\d{2})(\d{4})(\d{2})(\d{2})/, "$1.$2.$3 $4:$5"),
            "daytime",
          );

          if (isValid(parsedDate)) {
            onChange(parsedDate);
          }
        }
      } else {
        if (inputValue.length === 8) {
          const parsedDate = parseDate(inputValue.replace(/(\d{2})(\d{2})(\d{4})/, "$1.$2.$3"), "day");

          if (isValid(parsedDate)) {
            onChange(parsedDate);
          }
        }
      }
    } catch (error) {
      reportError("Invalid date format", error);
    }
  };

  return (
    <div className="relative max-w-[600px]">
      <PatternFormat
        tabIndex={tabIndex}
        value={formatValue()}
        format={getFormat()}
        onValueChange={handleValueChange}
        customInput={Input}
        placeholder={
          type === "daytime"
            ? language === "de"
              ? "TT.MM.JJJJ HH:mm"
              : "MM.DD.YYYY HH:mm"
            : language === "de"
              ? "TT.MM.JJJJ"
              : "MM.DD.YYYY"
        }
        patternChar="#"
        className={cn("[&>*]:placeholder:text-muted-foreground", "[&>*]:text-primary")}
      />
      <div className="absolute right-2 top-1 z-10">
        <Popover>
          <PopoverTrigger asChild>
            <Button size="icon" variant="ghost">
              <CalendarIcon className="text-blue-550" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto rounded-l border border-blue-300 bg-white p-0 shadow-md" align="center">
            <Calendar
              captionLayout="dropdown"
              fromYear={1910}
              toYear={new Date().getFullYear()}
              mode="single"
              selected={value}
              onSelect={(d) => onChange(d)}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

interface Props {
  type: "daytime" | "date";
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  tabIndex?: number;
}
